/**
 * How a link's target reads in "Verknüpfte Objekte" — its title, one meta line
 * and an icon kind — in the language the footer selects.
 *
 * The panel used to fall back to `cash_flow · cf-inv-26048c92-…`, and an id
 * means nothing to a farmer. An invoice, a cash flow or a cost operation has no
 * name, but the backend sends what it IS recognised by (`target_summary`: its
 * number, counterparty, date, amount), so the title is the name, else that
 * reference, else the kind of object. Nothing here ever prints `target_id`.
 */
import i18n from '../../i18n';
import type { DocumentLink } from '../../services/twinService';

export type TargetIconKind = 'receipt' | 'money' | 'contract' | 'land' | 'people' | 'machine' | 'object';

const ICON_KINDS: Readonly<Record<string, TargetIconKind>> = {
  invoice: 'receipt',
  settlement_event: 'receipt',
  delivery: 'receipt',
  delivery_event: 'receipt',
  event_line: 'receipt',
  cash_flow: 'money',
  financial_account: 'money',
  wage_ledger: 'money',
  parcel: 'land',
  field: 'land',
  crop_cycle: 'land',
  harvest: 'land',
  operation: 'land',
  soil_sample: 'land',
  nutrient_plan: 'land',
  plant_protection_application: 'land',
  seed_lot: 'land',
  person: 'people',
  organization: 'people',
  employment: 'people',
  machine: 'machine',
};

/** The icon family a target type belongs to; agreements of every kind are one. */
export function targetIconKind(entityType: string): TargetIconKind {
  if (entityType === 'agreement' || entityType.endsWith('_agreement')) return 'contract';
  return ICON_KINDS[entityType] ?? 'object';
}

/**
 * A type's name in the active language. A type this map does not know reads as
 * `fallback` (the lens registry's German name, when the caller has one), else a
 * neutral "Objekt" — never the raw `snake_case` type.
 */
export function entityTypeLabel(entityType: string, fallback?: string): string {
  const key = `twinLinks.entityTypes.${entityType}`;
  if (i18n.exists(key)) return i18n.t(key);
  return fallback || i18n.t('twinLinks.entityTypeFallback');
}

/** `de-DE`, or `en-GB` (day-first dates, like the rest of the English UI). */
export function displayLocale(): string {
  return i18n.language?.startsWith('en') ? 'en-GB' : 'de-DE';
}

/** Intl puts (narrow) no-break spaces around currency signs; a plain space reads the same and compares simply. */
function plainSpaces(text: string): string {
  return text.replace(/[\u00a0\u202f]/g, ' ');
}

export function formatDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const day = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  // `2026-13-45` would roll over into a real, wrong date: show it as sent instead.
  if (day.getUTCMonth() !== Number(m[2]) - 1 || day.getUTCDate() !== Number(m[3])) return iso;
  return new Intl.DateTimeFormat(displayLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(day);
}

export function formatMoney(amount: number, currency?: string): string {
  const code = (currency || 'EUR').toUpperCase();
  try {
    return plainSpaces(new Intl.NumberFormat(displayLocale(), { style: 'currency', currency: code }).format(amount));
  } catch {
    // Not an ISO 4217 code — the number in the active locale, then the code as sent.
    const number = new Intl.NumberFormat(displayLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${number.format(amount)} ${code}`;
  }
}

export interface LinkTargetDisplay {
  title: string;
  /** Shown joined by „ · " under the title; never repeats it. */
  meta: string[];
  kind: TargetIconKind;
  /** The title is only the type's word (no name, no reference): siblings may share it. */
  isFallback: boolean;
}

/** A `snake_case` value, as enum-valued lens titles render (an operation's `{properties.type}`). */
const ENUM_VALUE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

/**
 * The backend's name, unless it is an enum value: a translated word for a known
 * one ("cost_booking" → „Kostenbuchung"), nothing for an unknown `snake_case`
 * one — the title then falls back to the reference or the type's word. A plain
 * lowercase word without a translation is kept: it may be a real name.
 */
function readableName(raw: string | null | undefined): string | null {
  const name = raw?.trim();
  if (!name) return null;
  if (!ENUM_VALUE.test(name)) return name;
  const key = `twinLinks.enumValues.${name}`;
  if (i18n.exists(key)) return i18n.t(key);
  return name.includes('_') ? null : name;
}

export function linkTargetDisplay(link: DocumentLink, typeFallback?: string): LinkTargetDisplay {
  const summary = link.target_summary ?? {};
  const typeLabel = entityTypeLabel(link.target_type, typeFallback);
  const name = readableName(link.target_name);
  const title = name || summary.reference || typeLabel;
  const meta: string[] = [];
  const add = (part: string | null | undefined) => {
    if (part && part !== title && !meta.includes(part)) meta.push(part);
  };
  add(typeLabel);
  if (name) add(summary.reference);
  add(summary.counterparty);
  if (summary.date) add(formatDay(summary.date));
  if (typeof summary.amount === 'number') add(formatMoney(summary.amount, summary.currency));
  return { title, meta, kind: targetIconKind(link.target_type), isFallback: title === typeLabel };
}
